// ============================================
// CommandBuffer - Render Command Collection & Sorting
// ============================================

import {
  type RenderCommand,
  type CommandType,
  packSortKey,
} from '../types';

export type SortMode = 'none' | 'back-to-front' | 'front-to-back' | 'material';

/**
 * Collects render commands and sorts them for efficient execution
 */
export class CommandBuffer {
  private commands: RenderCommand[] = [];
  private sortMode: SortMode = 'back-to-front';
  private sorted = false;
  
  constructor(initialCapacity = 1024) {
    this.commands = new Array(initialCapacity);
    this.commands.length = 0;
  }
  
  /**
   * Add a command to the buffer
   */
  add(command: RenderCommand): void {
    this.commands.push(command);
    this.sorted = false;
  }
  
  /**
   * Set sort mode
   */
  setSortMode(mode: SortMode): void {
    this.sortMode = mode;
    this.sorted = false;
  }
  
  /**
   * Sort commands based on current sort mode
   */
  sort(): void {
    if (this.sorted || this.sortMode === 'none') return;
    
    switch (this.sortMode) {
      case 'back-to-front':
        // Sort by sortKey descending (higher z/layer first)
        this.commands.sort((a, b) => b.sortKey - a.sortKey);
        break;
        
      case 'front-to-back':
        // Sort by sortKey ascending (lower z/layer first)
        this.commands.sort((a, b) => a.sortKey - b.sortKey);
        break;
        
      case 'material':
        // Sort by sortKey to group by material (reduces state changes)
        this.commands.sort((a, b) => {
          // Extract material from sort key (lower 8 bits)
          const matA = a.sortKey & 0xFF;
          const matB = b.sortKey & 0xFF;
          if (matA !== matB) return matA - matB;
          // Then sort by z within same material
          return a.sortKey - b.sortKey;
        });
        break;
    }
    
    this.sorted = true;
  }
  
  /**
   * Clear all commands
   */
  clear(): void {
    this.commands.length = 0;
    this.sorted = false;
  }
  
  /**
   * Get command count
   */
  get length(): number {
    return this.commands.length;
  }
  
  /**
   * Iterate over commands
   */
  [Symbol.iterator](): Iterator<RenderCommand> {
    return this.commands[Symbol.iterator]();
  }
  
  /**
   * Get commands array (for direct iteration)
   */
  getCommands(): readonly RenderCommand[] {
    return this.commands;
  }
  
  /**
   * Merge another command buffer into this one
   */
  merge(other: CommandBuffer): void {
    for (const cmd of other.commands) {
      this.commands.push(cmd);
    }
    this.sorted = false;
  }
}

/**
 * DrawList - Higher-level draw call list with automatic batching hints
 */
export class DrawList {
  private entries: DrawEntry[] = [];
  
  /**
   * Add a draw entry
   */
  add(entry: DrawEntry): void {
    this.entries.push(entry);
  }
  
  /**
   * Sort entries for optimal batching
   */
  sort(): void {
    // Sort by: layer -> texture -> z -> blend
    this.entries.sort((a, b) => {
      if (a.layer !== b.layer) return a.layer - b.layer;
      if (a.texture !== b.texture) return a.texture - b.texture;
      if (a.z !== b.z) return a.z - b.z;
      return a.blendMode - b.blendMode;
    });
  }
  
  /**
   * Clear the list
   */
  clear(): void {
    this.entries.length = 0;
  }
  
  /**
   * Get entries
   */
  getEntries(): readonly DrawEntry[] {
    return this.entries;
  }
  
  /**
   * Entry count
   */
  get length(): number {
    return this.entries.length;
  }
  
  /**
   * Iterate entries
   */
  [Symbol.iterator](): Iterator<DrawEntry> {
    return this.entries[Symbol.iterator]();
  }
}

/**
 * A single draw entry (sprite, quad, etc.)
 */
export interface DrawEntry {
  // Sorting
  layer: number;
  z: number;
  
  // Material
  texture: number;
  blendMode: number;
  
  // Geometry (for sprites)
  x: number;
  y: number;
  width: number;
  height: number;
  
  // UV
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  
  // Color (packed RGBA)
  color: number;
  
  // Rotation (radians)
  rotation: number;
  
  // Anchor point (0-1)
  anchorX: number;
  anchorY: number;
}

/**
 * Create a draw entry with defaults
 */
export function createDrawEntry(partial: Partial<DrawEntry> = {}): DrawEntry {
  return {
    layer: partial.layer ?? 0,
    z: partial.z ?? 0,
    texture: partial.texture ?? 0,
    blendMode: partial.blendMode ?? 1,
    x: partial.x ?? 0,
    y: partial.y ?? 0,
    width: partial.width ?? 0,
    height: partial.height ?? 0,
    u0: partial.u0 ?? 0,
    v0: partial.v0 ?? 0,
    u1: partial.u1 ?? 1,
    v1: partial.v1 ?? 1,
    color: partial.color ?? 0xFFFFFFFF,
    rotation: partial.rotation ?? 0,
    anchorX: partial.anchorX ?? 0,
    anchorY: partial.anchorY ?? 0,
  };
}
